import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService } from '@upe/ngx-notification';
import { TypedJSON } from '@upe/typedjson';

import 'core-js/es6/map';
import 'rxjs/add/observable/of';

import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/toPromise';
import { Observable } from 'rxjs/Observable';
import { HttpService } from './http.service';

export interface IKeyValue<T> {
  [key: string]: T;
}

export type DistanceUnit = 'kilometers' | 'meters' | 'miles' | 'feet' | 'redians' | 'degrees';

export type Value = number | string | object;

export interface IPersistedModel {
  id: string;

  deserialize(json: IPersistedModel): void;

  serialize(): IPersistedModel;
}

export interface IFilter {
  fields?: Array<IKeyValue<boolean>>;
  include?: IIncludeFilter | string | string[] | Array<IKeyValue<string | string[]>>;
  limit?: number;
  order?: string | string[];
  skip?: number;
}

export interface IFieldFilter {
  gt?: number;
  gte?: number;
  it?: number;
  ite?: number;
  between?: number[];
  near?: number;
  maxDistance?: number;
  unit?: DistanceUnit;
  like?: string;
  nlike?: string;
  ilike?: string;
  nilike?: string;
  inq?: Value[];
  nin?: Value[];
  neq?: Value;
  reqexp?: string;
}

export interface IWhereFilter {
  or: IWhereFilter[];
  and: IWhereFilter[];

  [key: string]: IFieldFilter | Value;
}

export interface IIncludeFilter {
  relation: string;
  scope: IFilter;
}

export interface ICountResponse {
  count: number;
}

export interface IExistsResponse {
  exists: boolean;
}

@Injectable()
export class ApiService<PM extends IPersistedModel> extends HttpService {

  private readonly _cache: Map<string, PM> = new Map();

  public constructor(
    http: HttpClient,
    notification: NotificationService,
    path: string,
    private _constructor?: new () => PM,
  ) {
    super(http, notification, `/api${path}`);
    if (!this._constructor) {
      this.logger.warn('Create API service without model constructor!');
    }
  }

  public get(url: string): Observable<PM | null> {
    return this._get<PM>(url).map((response: PM) => this.handleDeserialize(response));
  }

  public patch(url: string, obj: PM): Observable<PM | null> {
    return this._patch<PM>(url, obj).map((response: PM) => this.handleDeserialize(response));
  }

  public post(url: string, obj: PM): Observable<PM | null> {
    return this._post<PM>(url, obj).map((response: PM) => this.handleDeserialize(response));
  }

  public put(url: string, obj: PM): Observable<PM | null> {
    return this._put<PM>(url, obj).map((response: PM) => this.handleDeserialize(response));
  }

  /**
   * Patch an existing model instance or insert a new one into the data source.
   * @param {PM} obj
   * @returns {Observable<PM | null>}
   */
  public patchOrCreate(obj: PM): Observable<PM | null> {
    return this.patch('', obj);
  }

  /**
   * Find all instances of the model matched by filter from the data source.
   * @param {IFilter} filter
   * @returns {Observable<PM[]>}
   */
  public findAll(filter?: IFilter): Observable<PM[]> {
    return this._getArray(filter ? `?filter=${JSON.stringify(filter)}` : '');
  }

  /**
   * Replace an existing model instance or insert a new one into the data source.
   * @param {PM} obj
   * @returns {Observable<PM | null>}
   */
  public replaceOrCreate(obj: PM): Observable<PM | null> {
    return this.put('', obj);
  }

  /**
   * Create a new instance of the model and persist it into the data source.
   * @param {PM} obj
   * @returns {Observable<PM | null>}
   */
  public create(obj: PM): Observable<PM | null> {
    return this.post('', obj);
  }

  /**
   * Patch attributes for a model instance and persist it into the data source.
   * @param {string} id
   * @param {PM} obj
   * @returns {Observable<PM | null>}
   */
  public patchById(id: string, obj: PM): Observable<PM | null> {
    return this.patch(`/${id}`, obj);
  }

  /**
   * Find a model instance by {{id}} from the data source.
   * @param {string} id
   * @returns {Observable<PM | null>}
   */
  public findById(id: string): Observable<PM | null> {
    return this._cache.has(id) ? Observable.of(this._cache.get(id) || null) : this.get(`/${id}`);
  }

  /**
   * Replace attributes for a model instance and persist it into the data source.
   * @param {string} id
   * @param {PM} obj
   * @returns {Observable<PM | null>}
   */
  public replaceAttributesById(id: string, obj: PM): Observable<PM | null> {
    return this.put(`/${id}`, obj);
  }

  /**
   * Delete a model instance by {{id}} from the data source.
   * @param {string} id
   * @returns {Observable<boolean>}
   */
  public deleteById(id: string): Observable<boolean> {
    return this._delete<{}>(`/${id}`)
      .map((response) => response !== null)
      .map((status: boolean) => status && this._cache.has(id) ? this._cache.delete(id) : status);
  }

  /**
   * Check whether a model instance exists in the data source.
   * @param {string} id
   * @returns {Observable<boolean>}
   */
  public exists(id: string): Observable<boolean> {
    return this._get<IExistsResponse>(`/${id}/exists`)
      .map((response: IExistsResponse) => response.exists);
  }

  /**
   * Count instances of the model matched by where from the data source.
   * @param where
   * @returns {Observable<number>}
   */
  public count(where?: IWhereFilter): Observable<number> {
    return this._get<ICountResponse>(`/count${where ? `?where=${JSON.stringify(where)}` : ''}`)
      .map((response: ICountResponse) => response.count);
  }

  /**
   * Find first instance of the model matched by filter from the data source.
   * @param {IFilter} filter
   * @returns {Observable<PM | null>}
   */
  public findOne(filter: IFilter): Observable<PM | null> {
    return this.get(`/findOne?filter=${JSON.stringify(filter)}`);
  }

  /**
   * Update instances of the model matched by {{where}} from the data source.
   * @param {PM} obj
   * @param {IWhere} where
   * @returns {Observable<number>}
   */
  public update(obj: PM, where: IWhereFilter): Observable<number> {
    return this._post<ICountResponse>(`/update?where=${JSON.stringify(where)}`, obj)
      .map((response: ICountResponse) => response.count);
  }

  protected _getArray(url: string): Observable<PM[]> {
    return this._get<PM[]>(url)
      .map((response: PM[]) => response ? response.map((item: PM) => this.handleDeserialize(item)) as PM[] : []);
  }

  protected deserialize(obj: PM): PM | null {
    return TypedJSON.parse(JSON.stringify(obj), this._constructor);
  }

  private handleDeserialize(obj: PM | null): PM | null {
    let model: PM | null = null;
    if (obj) {

      if (this._cache.has(obj.id)) {
        model = this._cache.get(obj.id) as PM;
        model.deserialize(obj);
      } else {

        try {
          // TODO : use new parse method with stringify
          model = this.deserialize(obj);

        } catch (e) {
          this.logger.error(e);

          return null;
        }
        if (model) {
          this._cache.set(model.id, model);
        }
      }

    }

    return model;
  }

}
